/*
 * Product add-ons: required validation, charged multi-item add-to-cart, and live summary.
 *
 * Add-on fields are rendered by snippets/main-product-addons.liquid, in four shapes:
 * a dropdown (select[data-addon-select], each option carrying data-variant-id and data-price
 * in cents), an agreement checkbox (input[data-addon-checkbox]), a free-text field
 * (input[data-addon-text]) and a file upload (input[data-addon-file]). Only dropdown options
 * can carry a price; the other three are always recorded as line item properties on the main
 * product line. Shopify does the uploading itself and turns the file property into a URL.
 *
 * - Required add-ons must be chosen before add-to-cart. The product form runs `novalidate`,
 *   so this enforces it (a delegated, capture-phase submit listener that runs before the
 *   theme's <product-form> handler on the same form). Text fields are compared trimmed, so
 *   whitespace alone does not pass.
 * - A selected add-on WITH a price is added to the cart as its own line item, so its price is
 *   actually charged. When any priced add-on is selected the whole add-to-cart is taken over
 *   here (main product + add-ons submitted together via /cart/add.js). A selected add-on with
 *   no price stays a property on the main product line, so the native flow can handle it.
 * - The live summary (snippets/main-product-addons-summary.liquid) mirrors the current
 *   selection: main product (unit x qty) + each priced add-on (flat), plus the subtotal.
 *
 * Everything is delegated / event-driven so it survives the variant-change re-renders that
 * replace parts of the product info column.
 */
(function () {
  if (window.__mainProductAddonsInit) return;
  window.__mainProductAddonsInit = true;

  var DEFAULT_PREFIX = "Please select an option for";

  /* ------------------------------------------------------------------ money */

  /*
   * Money output must follow the active Shopify Market, not the shop.
   *
   * Liquid price fields are already in the presentment currency, so the arithmetic here (minor
   * units) is correct per market. Formatting was not: it parsed `shop.money_format`, which is
   * fixed to the shop's currency, so a market presenting EUR or JPY got converted amounts
   * wearing the shop's symbol, separators and decimal count.
   *
   * The theme ships no JS money helper (every other price in Maranello is rendered server-side
   * by Liquid), so there is no built-in to reuse. Intl.NumberFormat, seeded with the presentment
   * currency and the request locale from Liquid, is used instead: it gets separators, decimal
   * places and symbol placement right for every market, including zero-decimal currencies such
   * as JPY and KRW where dividing by 100 was previously wrong.
   *
   * `formatMoneyLegacy` is kept as the fallback only.
   */
  var currencyFormatters = {};

  function currencyFormatter(currency, locale) {
    if (!currency || typeof Intl === "undefined" || !Intl.NumberFormat) return null;
    var key = currency + "|" + (locale || "");
    if (currencyFormatters[key] !== undefined) return currencyFormatters[key];
    var formatter = null;
    try {
      formatter = new Intl.NumberFormat(locale || undefined, {
        style: "currency",
        currency: currency,
      });
    } catch (error) {
      formatter = null;
    }
    currencyFormatters[key] = formatter;
    return formatter;
  }

  // `context` may be the [data-product-addons] / [data-addon-summary] host itself, or any
  // element inside one - both carry the currency, locale and legacy format from Liquid.
  var MONEY_HOST = "[data-product-addons], [data-addon-summary]";

  function money(cents, context) {
    var host = null;
    if (context) {
      host =
        context.matches && context.matches(MONEY_HOST)
          ? context
          : context.closest && context.closest(MONEY_HOST);
    }
    var data = host ? host.dataset : {};
    var formatter = currencyFormatter(data.currency, data.locale);
    var amount = Number(cents) || 0;

    if (formatter) {
      // Minor units -> major units using the currency's own decimal count, so zero-decimal
      // currencies are not divided by 100.
      var digits = formatter.resolvedOptions().maximumFractionDigits;
      return formatter.format(amount / Math.pow(10, digits));
    }

    return formatMoneyLegacy(amount, data.moneyFormat);
  }

  function formatMoneyLegacy(cents, format) {
    var formatString = format || "${{amount}}";
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var amount = Number(cents) || 0;

    function number(precision, thousands, decimal) {
      thousands = thousands || ",";
      decimal = decimal || ".";
      var value = (amount / 100).toFixed(precision);
      var parts = value.split(".");
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : "");
    }

    var match = formatString.match(placeholder);
    var value;
    switch (match && match[1]) {
      case "amount_no_decimals":
        value = number(0);
        break;
      case "amount_with_comma_separator":
        value = number(2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = number(0, ".", ",");
        break;
      case "amount_with_space_separator":
        value = number(2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = number(0, " ", "");
        break;
      case "amount_with_apostrophe_separator":
        value = number(2, "'", ".");
        break;
      default:
        value = number(2);
    }
    // Strip any HTML the money format wraps around the amount so it is safe as textContent.
    return formatString.replace(placeholder, value).replace(/<[^>]*>/g, "");
  }

  /* ------------------------------------------------------------- validation */

  function labelFor(el) {
    var field = el.closest(".pdp-opt");
    var label = field && field.querySelector(".pdp-opt__label");
    return label ? label.textContent.replace(/\*/g, "").trim() : "";
  }

  function errorPrefix(el) {
    var container = el.closest("[data-product-addons]");
    return (container && container.dataset.addonErrorPrefix) || DEFAULT_PREFIX;
  }

  // Required add-ons that are not satisfied: empty selects + unchecked checkboxes + blank text.
  function invalidSelectsForForm(form) {
    var invalid = [];
    document.querySelectorAll('select[data-addon-required="true"]').forEach(function (select) {
      if (select.form === form && select.value === "") invalid.push(select);
    });
    document
      .querySelectorAll('input[type="checkbox"][data-addon-required="true"]')
      .forEach(function (checkbox) {
        if (checkbox.form === form && !checkbox.checked) invalid.push(checkbox);
      });
    // Text add-ons are trimmed before the check: whitespace alone satisfies the native
    // `required` attribute but is not a usable answer to print on the order.
    document
      .querySelectorAll('input[data-addon-text][data-addon-required="true"]')
      .forEach(function (input) {
        if (input.form === form && input.value.trim() === "") invalid.push(input);
      });
    // File add-ons: a file must actually be picked.
    document
      .querySelectorAll('input[data-addon-file][data-addon-required="true"]')
      .forEach(function (input) {
        if (input.form === form && input.files.length === 0) invalid.push(input);
      });
    return invalid;
  }

  // Whether this form carries a file the customer picked. Decides how the cart request has to
  // be built - see multiAddToCart.
  function selectedFileInputs(form) {
    var withFiles = [];
    document.querySelectorAll("input[data-addon-file]").forEach(function (input) {
      if (input.form === form && input.files.length) withFiles.push(input);
    });
    return withFiles;
  }

  function showFormError(form, message) {
    var host = form.closest("product-form");
    if (!host) return;
    var wrapper = host.querySelector(".product-form-error-message-wrapper");
    var messageEl = host.querySelector(".product-form-error-message");
    if (!wrapper || !messageEl) return;
    messageEl.textContent = message;
    wrapper.removeAttribute("hidden");
  }

  function clearFormError(form) {
    var host = form.closest("product-form");
    var wrapper = host && host.querySelector(".product-form-error-message-wrapper");
    if (wrapper) wrapper.setAttribute("hidden", "");
  }

  /* ------------------------------------------------------------------- cart */

  // Selected add-ons attached to the given form, split by whether they carry a price.
  function selectedAddons(form) {
    var priced = [];
    document.querySelectorAll("select[data-addon-select]").forEach(function (select) {
      if (select.form !== form) return;
      var option = select.selectedOptions[0];
      if (!option || option.value === "") return;
      var price = Number(option.dataset.price) || 0;
      var variantId = option.dataset.variantId;
      if (price > 0 && variantId) {
        priced.push({
          select: select,
          variantId: variantId,
          name: select.dataset.addonName || "",
          variantTitle: option.value,
          price: price,
        });
      }
    });
    return priced;
  }

  // An optional add-on the customer never touched still carries `name="properties[…]"` with an
  // empty value, so `new FormData(form)` in product-form.js serialises `properties[Name]=""`
  // and a blank line-item property rides along into the cart and the order. The takeover path
  // below already filters empty values (`value !== ""`), so only the native path was affected -
  // the two disagreed. Disabled controls are never serialised, so the empty ones are disabled
  // for the duration of this submit and restored on the next tick.
  //
  // Safe against the rest of this file: `selectedAddons()` skips empty selects anyway, and the
  // native <select> is visually replaced by the pdp-kit listbox (.odd--ready .odd__native), so
  // there is nothing on screen to flicker.
  function suppressEmptyAddonProperties(form) {
    var suppressed = [];
    document.querySelectorAll("select[data-addon-select]").forEach(function (select) {
      if (select.form === form && select.value === "" && !select.disabled) {
        select.disabled = true;
        suppressed.push(select);
      }
    });
    // Same for an optional text add-on left blank. Trimmed, so a field holding only spaces is
    // suppressed too - otherwise `properties[Name]="   "` survives both paths (the takeover
    // below only filters exactly `""`) and shows as an empty-looking property on the order.
    document.querySelectorAll("input[data-addon-text]").forEach(function (input) {
      if (input.form === form && input.value.trim() === "" && !input.disabled) {
        input.disabled = true;
        suppressed.push(input);
      }
    });
    // And an optional file add-on nobody picked a file for. An empty file input still
    // serialises into FormData - as a zero-byte part with a blank filename - which would reach
    // the order as an empty property.
    document.querySelectorAll("input[data-addon-file]").forEach(function (input) {
      if (input.form === form && input.files.length === 0 && !input.disabled) {
        input.disabled = true;
        suppressed.push(input);
      }
    });
    if (!suppressed.length) return;
    setTimeout(function () {
      suppressed.forEach(function (field) {
        field.disabled = false;
      });
    }, 0);
  }

  function productTitleFor(select) {
    var container = select.closest("[data-product-addons]");
    return (container && container.dataset.productTitle) || "";
  }

  function multiAddToCart(form, priced) {
    var host = form.closest("product-form");
    var submitButton = (host || form).querySelector('[type="submit"]');
    if (submitButton && submitButton.classList.contains("loading")) return;
    var spinner = host && host.querySelector(".button-overlay-spinner");
    var minicart = document.querySelector("mini-cart");

    var data = new FormData(form);
    var mainProperties = {};
    var cartAttributes = {};
    data.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match) {
        if (value !== "") mainProperties[match[1]] = value;
        return;
      }
      // Cart attributes belong at the body level, not on the line item. Apps and custom
      // fields use these, and rebuilding the request by hand used to drop them.
      var attribute = key.match(/^attributes\[(.+)\]$/);
      if (attribute && value !== "") cartAttributes[attribute[1]] = value;
    });
    // Priced add-ons are added as their own (hidden) line items so they are charged, but
    // they are ALSO shown as an enriched property on the main line so the customer sees the
    // choice and its upcharge (the add-on lines themselves are hidden in the cart).
    priced.forEach(function (addon) {
      mainProperties[addon.name] =
        addon.variantTitle + " (+" + money(addon.price, addon.select) + ")";
    });

    // Date.now() alone collides when two submits land in the same millisecond, which would
    // merge two shoppers' bundles in a shared-session edge case (and any future grouping that
    // keys off _bundle_id).
    var bundleId = "addon-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
    mainProperties._bundle_id = bundleId;

    var mainQuantity = Number(data.get("quantity")) || 1;
    var mainItem = {
      id: data.get("id"),
      quantity: mainQuantity,
      properties: mainProperties,
    };
    // Subscriptions: this request is rebuilt by hand rather than posting the form, so any
    // selling plan chosen (native or injected by the Subscription Cart app) has to be carried
    // over explicitly or the product would silently be added as a one-off purchase.
    var sellingPlan = data.get("selling_plan");
    if (sellingPlan) mainItem.selling_plan = sellingPlan;

    var addonItems = [];
    var productTitle = priced.length ? productTitleFor(priced[0].select) : "";
    // Add-on quantity tracks the main product's: 3 kits with a coating selected means 3
    // coatings. Previously hardcoded to 1, so only one add-on was ever charged no matter how
    // many of the parent product were ordered.
    priced.forEach(function (addon) {
      addonItems.push({
        id: addon.variantId,
        quantity: mainQuantity,
        properties: { "Add-on for": productTitle, _bundle_id: bundleId, _addon: "true" },
      });
    });

    var sections = null;
    if (minicart) {
      sections = minicart.getSectionsToRender().map(function (section) {
        return section.section;
      });
    }

    function baseConfig() {
      var config =
        typeof fetchConfig === "function"
          ? fetchConfig("javascript")
          : { method: "POST", headers: { Accept: "application/javascript" } };
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      return config;
    }

    function postJson(payload) {
      if (Object.keys(cartAttributes).length) payload.attributes = cartAttributes;
      if (sections) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }
      var config = baseConfig();
      config.headers["Content-Type"] = "application/json";
      config.body = JSON.stringify(payload);
      return fetch(window.routes.cart_add_url, config).then(function (response) {
        return response.json();
      });
    }

    /*
     * A file upload forces the request into two parts.
     *
     * /cart/add.js takes EITHER one form-encoded item, which can carry a file, OR a JSON
     * "items" array, which cannot - a file has no JSON representation. Everything below used
     * to go as one JSON request, so an upload picked alongside a priced add-on was silently
     * dropped. When there is a file the main line is posted on its own as multipart (the
     * browser sets the boundary, hence deleting Content-Type), and the add-on lines follow
     * as JSON. Without a file nothing changes: one JSON request as before.
     *
     * Not atomic, so the main line is checked before the add-ons are sent - a failure there
     * stops us adding add-on lines with no parent to attach to.
     */
    var files = selectedFileInputs(form);
    var request;

    if (files.length === 0) {
      request = postJson({ items: [mainItem].concat(addonItems) });
    } else {
      var mainData = new FormData(form);
      priced.forEach(function (addon) {
        mainData.set("properties[" + addon.name + "]", mainProperties[addon.name]);
      });
      mainData.set("properties[_bundle_id]", bundleId);
      // Cart attributes are body-level on the JSON call and not part of the form payload,
      // so strip them here and let postJson carry them.
      var attrKeys = [];
      mainData.forEach(function (value, key) {
        if (key.indexOf("attributes[") === 0) attrKeys.push(key);
      });
      attrKeys.forEach(function (key) {
        mainData.delete(key);
      });

      var multipart = baseConfig();
      delete multipart.headers["Content-Type"];
      multipart.body = mainData;

      request = fetch(window.routes.cart_add_url, multipart)
        .then(function (response) {
          return response.json();
        })
        .then(function (first) {
          if (first.status) return first;
          return postJson({ items: addonItems });
        });
    }

    clearFormError(form);
    if (submitButton) {
      submitButton.setAttribute("aria-disabled", true);
      submitButton.classList.add("loading");
    }
    if (spinner) spinner.classList.remove("hidden");

    request
      .then(function (response) {
        if (response.status) {
          showFormError(form, response.description || response.message || "");
          if (minicart) minicart.updateSectionContents();
          return;
        }
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartItemAdd, { source: "product-addons", items: [response] });
        }
        if (minicart) {
          minicart.renderContents(response);
          if (window.themeSettings && window.themeSettings.redirectToCart) {
            location.href = window.routes.cart_url;
          } else {
            minicart.open(submitButton);
          }
        } else {
          location.href = window.routes.cart_url;
        }
      })
      .catch(function (error) {
        console.error(error);
      })
      .finally(function () {
        if (submitButton) {
          submitButton.classList.remove("loading");
          submitButton.removeAttribute("aria-disabled");
        }
        if (spinner) spinner.classList.add("hidden");
      });
  }

  /* ---------------------------------------------------------------- summary */

  function renderSummary(summaryEl) {
    var listEl = summaryEl.querySelector("[data-addon-summary-list]");
    var totalEl = summaryEl.querySelector("[data-addon-summary-total]");
    if (!listEl || !totalEl) return;

        var unitPrice = Number(summaryEl.dataset.productPrice) || 0;
    var title = summaryEl.dataset.productTitle || "";

    var block = summaryEl.closest(".product-block");
    var controls = block
      ? block.querySelectorAll(
          "select[data-addon-select], input[data-addon-checkbox], input[data-addon-text], input[data-addon-file]",
        )
      : [];
    // Falls back to the variant picker so the quantity is still read on a product whose add-ons
    // metafield is set but yields no usable field (every entry missing a name, say) - the
    // summary still renders, and without this its subtotal would be stuck at quantity 1.
    var form = controls.length ? controls[0].form : null;
    if (!form) {
      var anyVariantSelect = document.querySelector("select[data-variant-option-name]");
      form = anyVariantSelect ? anyVariantSelect.form : null;
    }
    var qtyInput = form && form.querySelector('[name="quantity"]');
    var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    var rows = [];
    var mainTotal = unitPrice * qty;
    rows.push({ label: qty + "× " + title, price: money(mainTotal, summaryEl), main: true });

    var subtotal = mainTotal;
    var selectedCount = 0;

    // The chosen variant, listed directly under the main product and above the add-ons: one row
    // per product option, "<option name> - <value>".
    //
    // Never priced and never added to the subtotal - the variant's price IS the main row's
    // `unitPrice`, so charging it again here would double it. The price cell shows the same en
    // dash as a free add-on.
    //
    // Scoped to the section rather than to `block`: pdp-kit.js moves the picker into the kit
    // panel (making it a descendant of this summary's .product-block), but that move and this
    // file's first render both happen on DOMContentLoaded, so relying on it would make the
    // opening render order-dependent. There is exactly one picker per product section.
    var section = summaryEl.closest(".shopify-section");
    var variantSelects = section
      ? section.querySelectorAll("select[data-variant-option-name]")
      : [];
    variantSelects.forEach(function (select) {
      var option = select.selectedOptions[0];
      if (!option || option.value === "") return;
      selectedCount++;
      rows.push({
        label: select.dataset.variantOptionName + " - " + option.value,
        price: "–", // en dash
      });
    });

    controls.forEach(function (control) {
      var value;
      var price;
      if (control.tagName === "SELECT") {
        var option = control.selectedOptions[0];
        if (!option || option.value === "") return;
        value = option.value;
        price = Number(option.dataset.price) || 0;
      } else if (control.type === "checkbox") {
        // Agreement checkbox — only counts when checked; never priced.
        if (!control.checked) return;
        value = control.value;
        price = 0;
      } else if (control.type === "file") {
        // File add-on — shows the chosen filename. Never priced; Shopify stores the upload
        // and turns the property into a URL at add-to-cart time.
        if (control.files.length === 0) return;
        value = control.files[0].name;
        price = 0;
      } else {
        // Text add-on — only counts once something has been typed; never priced (a typed
        // answer has no variant behind it, so it stays a property on the main line).
        value = control.value.trim();
        if (value === "") return;
        price = 0;
      }
      selectedCount++;
      var label = (control.dataset.addonName || "") + " - " + value;
      // An option only affects the price WHILE it is selected: a positive add-on adds, a
      // discount/credit option (negative price) subtracts. Deselecting it reverts the price.
      //
      // Priced options scale with the main quantity, matching how they are now added to the
      // cart (one add-on line item per unit of the parent). The row therefore shows the
      // extended amount and carries the same `qty ×` prefix as the main row above, so the
      // figure cannot be misread as a per-unit price. Free options are left plain: they never
      // become their own line item, so nothing scales.
      if (price !== 0) {
        var lineTotal = price * qty;
        subtotal += lineTotal;
        var priceStr = money(Math.abs(lineTotal), summaryEl);
        rows.push({
          label: qty + "× " + label,
          price: (lineTotal < 0 ? "-" : "") + priceStr,
        });
      } else {
        rows.push({ label: label, price: "–" }); // en dash
      }
    });

    // Only show the summary once at least one add-on option is selected.
    summaryEl.hidden = selectedCount === 0;

    listEl.textContent = "";
    rows.forEach(function (row) {
      var li = document.createElement("li");
      li.className = "addon-summary-row" + (row.main ? " addon-summary-row-main" : "");
      var labelSpan = document.createElement("span");
      labelSpan.className = "addon-summary-label";
      labelSpan.textContent = row.label;
      var priceSpan = document.createElement("span");
      priceSpan.className = "addon-summary-price";
      priceSpan.textContent = row.price;
      li.appendChild(labelSpan);
      li.appendChild(priceSpan);
      listEl.appendChild(li);
    });

    totalEl.textContent = money(subtotal, summaryEl);

    // Main product price always mirrors the summary subtotal.
    // Compare-at tracks the quantity so the struck figure stays a like-for-like comparison
    // against the live price. 0 when the variant has no compare-at.
    var compareAtUnit = Number(summaryEl.dataset.compareAtPrice) || 0;
    setMainPrice(subtotal, compareAtUnit * qty, summaryEl);
  }

  function renderAllSummaries() {
    document.querySelectorAll("[data-addon-summary]").forEach(renderSummary);
  }

  /* --------------------------- on-load discount-amount price bump (display) */

  function firstTextNode(el) {
    for (var i = 0; i < el.childNodes.length; i++) {
      var node = el.childNodes[i];
      if (node.nodeType === 3 && node.textContent.trim() !== "") return node;
    }
    return null;
  }

  // Keep the main product price block in sync with the summary subtotal.
  // Display-only — the real product/variant/line-item price is untouched.
  function setMainPrice(cents, compareCents, summaryEl) {
    var scope = summaryEl.closest(".product-page") || document;
    var priceBlock = scope.querySelector(".product-block-price .price");
    if (!priceBlock) return;

    var formatted = money(cents, summaryEl);

    // Commented out with the compare-at price itself: snippets/price.liquid no longer renders
    // the `.price-sale` block at all, so there is no struck-through figure left to keep in step
    // with the quantity and this selector matches nothing. Kept for rollback alongside that
    // markup - restore both together.
    //
    // The compare-at is scaled by quantity only - never by the add-ons. Add-ons are not
    // discounted, so folding them in would overstate the saving; keeping it at
    // `compare_at_price × qty` means the gap between the two figures stays the true saving on
    // the product itself. Skipped when the variant has no compare-at (0), and guarded on the
    // element existing at all, since snippets/price.liquid only renders `.price-sale` when
    // `compare_at_price > price`.
    //
    // if (compareCents > 0) {
    //   var compareFormatted = money(compareCents, summaryEl);
    //   priceBlock.querySelectorAll(".price-sale .price-item-regular").forEach(function (el) {
    //     var compareNode = firstTextNode(el);
    //     if (compareNode) compareNode.textContent = compareFormatted;
    //   });
    // }

    // Only the CURRENT price is rewritten - never the compare-at.
    //
    // `.price-item-regular` appears TWICE in snippets/price.liquid: once inside
    // `.price-regular` (line 59) as the price shown when the product is not on sale, and again
    // inside `.price-sale` (line 73) as the struck-through
    // `<s class="price-item price-item-regular">` that holds `compare_at_price`. The previous
    // selector matched both, so the add-on subtotal was written straight over Shopify's
    // compare-at value and it always rendered identical to the live price.
    //
    // Scoping by parent hits the live price in both states and leaves the compare-at exactly
    // as configured in the backend for the selected variant.
    priceBlock
      .querySelectorAll(".price-regular .price-item-regular, .price-sale .price-item-sale")
      .forEach(function (el) {
        var node = firstTextNode(el);
        if (node) node.textContent = formatted;
      });
  }

  /* ------------------------------------------------------------- listeners */

  // Add-to-cart: validate required, then take over the submit when priced add-ons are chosen.
  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (!form || form.dataset.type !== "add-to-cart-form") return;

      var invalid = invalidSelectsForForm(form);
      if (invalid.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        invalid.forEach(function (select) {
          select.setAttribute("aria-invalid", "true");
        });
        // No panel to reveal any more - the kit is always open, so the missing field is already
        // on screen. The focus() below scrolls it into view on its own.
        showFormError(form, errorPrefix(invalid[0]) + " " + labelFor(invalid[0]));
        var oddBtn = invalid[0].closest(".odd") && invalid[0].closest(".odd").querySelector(".odd__btn");
        (oddBtn || invalid[0]).focus();
        return;
      }

      // Runs for both paths: this listener is capture-phase on document, so it completes before
      // product-form.js builds its FormData in the bubble phase at the form.
      suppressEmptyAddonProperties(form);

      var priced = selectedAddons(form);
      if (priced.length === 0) return; // native flow handles main product + free-property add-ons

      event.preventDefault();
      event.stopImmediatePropagation();
      multiAddToCart(form, priced);
    },
    true,
  );

  // `select[data-variant-option-name]` is the theme's own variant picker. It carries no
  // `data-addon-*` attribute, so it only ever reaches renderAllSummaries() here — validation,
  // selectedAddons() and suppressEmptyAddonProperties() all key off `data-addon-*` and skip it.
  // Listening here just redraws the variant row on the customer's own pick, without waiting for
  // the section fetch that follows it.
  var ADDON_CONTROLS =
    'select[data-addon-select], input[type="checkbox"][data-addon-checkbox], input[data-addon-text], input[data-addon-file], select[data-variant-option-name]';

  // Shared by the `change` listener (selects, checkboxes) and the `input` one (text fields):
  // clear the field's invalid state once it is satisfied, then redraw the summary.
  function handleAddonInteraction(target) {
    if (target.matches('[data-addon-required="true"]')) {
      var satisfied;
      if (target.type === "checkbox") {
        satisfied = target.checked;
      } else if (target.type === "file") {
        satisfied = target.files.length > 0;
      } else if (target.matches("input[data-addon-text]")) {
        // Trimmed to match invalidSelectsForForm, so spaces alone keep the field invalid.
        satisfied = target.value.trim() !== "";
      } else {
        satisfied = target.value !== "";
      }
      if (satisfied) target.removeAttribute("aria-invalid");
      var form = target.form;
      if (form && invalidSelectsForForm(form).length === 0) clearFormError(form);
    }
    renderAllSummaries();
  }

  document.addEventListener("change", function (event) {
    var target = event.target;
    if (!target.matches || !target.matches(ADDON_CONTROLS)) return;
    handleAddonInteraction(target);
  });

  // Quantity typed directly (the +/- buttons publish productQuantityUpdate below), plus text
  // add-ons: those need the summary and the required state to follow each keystroke rather than
  // waiting for blur, which is when a text input fires `change`.
  document.addEventListener("input", function (event) {
    var target = event.target;
    if (!target) return;
    if (target.name === "quantity") {
      renderAllSummaries();
      return;
    }
    if (target.matches && target.matches("input[data-addon-text]")) {
      handleAddonInteraction(target);
    }
  });

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.productQuantityUpdate, renderAllSummaries);
    subscribe(PUB_SUB_EVENTS.productVariantChange, function (data) {
      if (!data || !data.variant) return;
      var scope = document.getElementById("ProductPage-" + data.sectionId) || document;
      scope.querySelectorAll("[data-addon-summary]").forEach(function (summaryEl) {
        summaryEl.dataset.productPrice = data.variant.price;
        // Kept in step with the price: a variant with no compare-at reports null, which must
        // land as 0 so the previous variant's compare-at is not carried over.
        summaryEl.dataset.compareAtPrice = data.variant.compare_at_price || 0;
        renderSummary(summaryEl);
      });
    });
  }

  function init() {
    // renderAllSummaries computes each subtotal and syncs the main price block to it.
    // Runs after pdp-kit.js (loaded first) has built the option list items.
    renderAllSummaries();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
